import { Group, Stack, Text } from "@mantine/core"
import {
  IconBuildingFactory2,
  IconFileCheck,
  IconShirt,
} from "@tabler/icons-react"
import { SegmentedBadge, Tooltip } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { SupplierDto } from "~/gen/api"

type RequestCounts = {
  OPENED?: number
  CLOSED?: number
  SUBMITTED?: number
  REJECTED?: number
  ACCEPTED?: number
}

type CertificateRequestCounts = Record<
  string,
  Record<"OPENED" | "SUBMITTED" | "REJECTED" | "ACCEPTED", number>
>

type SupplierWithRequestCounts = SupplierDto & {
  informationRequestsCount?: RequestCounts
  certificateRequestsCount?: CertificateRequestCounts
  productionSitesRequestsCount?: RequestCounts
}

const calculateRequestRatio = (
  counts: RequestCounts | undefined
): {
  answered: number
  total: number
  ratio: number
} => {
  if (!counts) {
    return { answered: 0, total: 0, ratio: 0 }
  }

  const total = Object.values(counts).reduce(
    (sum, count) => sum + (count || 0),
    0
  )

  const answered = counts.CLOSED || 0
  const ratio = total > 0 ? answered / total : 0

  return { answered: answered || 0, total: total || 0, ratio: ratio || 0 }
}

const calculateCertificateRequestRatio = (
  counts: CertificateRequestCounts | undefined
): {
  answered: number
  total: number
  ratio: number
  details: Array<{ name: string; answered: number; total: number }>
} => {
  if (!counts) {
    return { answered: 0, total: 0, ratio: 0, details: [] }
  }

  let totalAnswered = 0
  let totalCount = 0
  const details: Array<{ name: string; answered: number; total: number }> = []

  for (const [certificateName, statusCounts] of Object.entries(counts)) {
    const certificateTotal = Object.values(statusCounts).reduce(
      (sum, count) => sum + (count || 0),
      0
    )
    const certificateAnswered =
      (statusCounts.SUBMITTED || 0) +
      (statusCounts.REJECTED || 0) +
      (statusCounts.ACCEPTED || 0)

    totalAnswered += certificateAnswered
    totalCount += certificateTotal

    if (certificateTotal > 0) {
      details.push({
        name: certificateName,
        answered: certificateAnswered,
        total: certificateTotal,
      })
    }
  }

  const ratio = totalCount > 0 ? totalAnswered / totalCount : 0

  // Sort details alphabetically by name
  const sortedDetails = details.sort((a, b) => a.name.localeCompare(b.name))

  return {
    answered: totalAnswered,
    total: totalCount,
    ratio,
    details: sortedDetails,
  }
}

export const RequestsRatioCell = ({
  supplier,
}: {
  supplier: SupplierWithRequestCounts
}) => {
  const { t } = useTranslation("traceability")

  const infoRatio = calculateRequestRatio(supplier.informationRequestsCount)
  const certRatio = calculateCertificateRequestRatio(
    supplier.certificateRequestsCount
  )
  const psiteRatio = calculateRequestRatio(
    supplier.productionSitesRequestsCount
  )

  return (
    <Group
      gap="var(--mantine-spacing-xxs)"
      wrap="nowrap"
      style={{ width: "fit-content" }}
    >
      <SegmentedBadge
        items={[
          <Tooltip
            key="products"
            label={`${t("contactTable.columns.productRequests.name")}: ${infoRatio.answered}/${infoRatio.total}`}
          >
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <IconShirt size={12} color="var(--mantine-color-gray-6)" />
              <Text fz="xs">
                {infoRatio.answered}/{infoRatio.total}
              </Text>
            </Group>
          </Tooltip>,
          <Tooltip
            key="documents"
            label={
              certRatio.details.length > 0 ? (
                <Stack gap="xs">
                  {t("contactTable.columns.certificateRequests.name")} :{" "}
                  {certRatio.answered}/{certRatio.total}
                  {certRatio.details.map((detail) => (
                    <span key={detail.name}>
                      {detail.name}: {detail.answered}/{detail.total}{" "}
                      {t(
                        "contactTable.columns.productionSiteRequests.received"
                      )}
                    </span>
                  ))}
                </Stack>
              ) : (
                `${t("contactTable.columns.certificateRequests.name")}: ${certRatio.answered}/${certRatio.total}`
              )
            }
          >
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <IconFileCheck size={12} color="var(--mantine-color-gray-6)" />
              <Text fz="xs">
                {certRatio.answered}/{certRatio.total}
              </Text>
            </Group>
          </Tooltip>,
          <Tooltip
            key="psites"
            label={`${t("contactTable.columns.productionSiteRequests.name")}: ${psiteRatio.answered}/${psiteRatio.total}`}
          >
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <IconBuildingFactory2
                size={12}
                color="var(--mantine-color-gray-6)"
              />
              <Text fz="xs">
                {psiteRatio.answered}/{psiteRatio.total}
              </Text>
            </Group>
          </Tooltip>,
        ]}
      />
    </Group>
  )
}
